import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { submitContactForm } from '../../../lib/zoho';

const CallToAction = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const validateForm = () => {
    const errors = {
      fullName: '',
      email: '',
      phone: '',
      message: ''
    };
    let isValid = true;

    // Name validation
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
      isValid = false;
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!formData.email.includes('@')) {
      errors.email = 'Email is required';
      isValid = false;
    }

    // Phone validation
    const phoneRegex = /^\+?\d+$/;
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else if (!phoneRegex.test(formData.phone)) {
      errors.phone = 'Phone number is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const phoneNumber = formData.phone.startsWith('+') ? formData.phone : `+${formData.phone}`;
      
      await submitContactForm({
        ...formData,
        phone: phoneNumber,
        countryCode: '' // We'll handle the full phone number in the phone field
      });

      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your message. We\'ll be in touch soon!'
      });
      setFormData({ fullName: '', email: '', phone: '', message: '' });
      setFormErrors({ fullName: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Something went wrong. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="relative py-24 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black/90" />
      </div>
      
      <div className="container mx-auto px-4 relative">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Left Column - Text */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <Mail className="w-6 h-6 text-accent" />
                <span className="text-accent">Get in touch</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-semibold text-white mb-6">
                Don't be shy,<br />
                just say hi.
              </h2>
              <p className="text-xl text-gray-400 mb-8">
                Ready to transform your digital presence? Let's start a conversation about your next project.
              </p>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-accent" />
                <a 
                  href="mailto:info@solutions21.ae" 
                  className="text-white hover:text-accent transition-colors duration-300"
                >
                  info@solutions21.ae
                </a>
              </div>
            </motion.div>

            {/* Right Column - Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitStatus.type && (
                  <div className={cn(
                    "p-4 rounded-lg",
                    submitStatus.type === 'success' ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"
                  )}>
                    {submitStatus.message}
                  </div>
                )}

                <div>
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, fullName: e.target.value }));
                      if (formErrors.fullName) setFormErrors(prev => ({ ...prev, fullName: '' }));
                    }}
                    className={cn(
                      "w-full bg-transparent border-b py-4 text-white placeholder-white/60",
                      formErrors.fullName ? "border-red-500" : "border-white/20",
                      "focus:outline-none focus:border-accent",
                      "transition-colors duration-300"
                    )}
                  />
                  {formErrors.fullName && (
                    <p className="mt-2 text-sm text-red-500">{formErrors.fullName}</p>
                  )}
                </div>

                <div>
                  <input
                    type="text"
                    placeholder="Phone number (e.g. +971...)"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d+]/g, '');
                      setFormData(prev => ({ ...prev, phone: value }));
                      if (formErrors.phone) setFormErrors(prev => ({ ...prev, phone: '' }));
                    }}
                    className={cn(
                      "w-full bg-transparent border-b py-4 text-white placeholder-white/60",
                      formErrors.phone ? "border-red-500" : "border-white/20",
                      "focus:outline-none focus:border-accent",
                      "transition-colors duration-300"
                    )}
                  />
                  {formErrors.phone && (
                    <p className="mt-2 text-sm text-red-500">{formErrors.phone}</p>
                  )}
                </div>

                <div>
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, email: e.target.value }));
                      if (formErrors.email) setFormErrors(prev => ({ ...prev, email: '' }));
                    }}
                    className={cn(
                      "w-full bg-transparent border-b py-4 text-white placeholder-white/60",
                      formErrors.email ? "border-red-500" : "border-white/20",
                      "focus:outline-none focus:border-accent",
                      "transition-colors duration-300"
                    )}
                  />
                  {formErrors.email && (
                    <p className="mt-2 text-sm text-red-500">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <textarea
                    placeholder="Message (optional)"
                    value={formData.message}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, message: e.target.value }));
                      if (formErrors.message) setFormErrors(prev => ({ ...prev, message: '' }));
                    }}
                    rows={4}
                    className={cn(
                      "w-full bg-transparent border-b py-4 text-white placeholder-white/60",
                      formErrors.message ? "border-red-500" : "border-white/20",
                      "focus:outline-none focus:border-accent",
                      "transition-colors duration-300 resize-none"
                    )}
                  />
                  <div className="flex justify-end mt-2">
                    <span className="text-sm text-white/40">1000 characters</span>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    "group flex items-center gap-2 px-8 py-4 bg-accent rounded-lg text-white font-medium w-full justify-center",
                    "hover:bg-accent/90 transition-colors duration-300",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  <span>{isSubmitting ? 'Submitting...' : 'Submit'}</span>
                  <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;